import React, { useEffect, useState, useRef } from 'react';
import Icon from 'components/Icon';
import clx from 'classnames';
import Button, { Size, Variants } from 'components/Button';
import { trackEvent } from 'utils/segment';
import { useParams } from 'react-router-dom';
import useAuth from 'hooks/useAuth';

type StepStatus = 'queued' | 'in_progress' | 'created' | 'failed';

type Step = {
  id: string;
  label: string;
  icon: string;
};

type EstimateToCreate<T> = {
  id: number;
  name: string;
  formData: T;
};

type StepProgressCallback = (
  stepId: string,
  status: StepStatus,
  error?: string
) => void;

type EstimateCreationProgressProps<T> = {
  estimates: EstimateToCreate<T>[];
  /**
   * Function to create an estimate.
   * If steps are provided, this function receives a progress callback
   * to report progress on individual steps.
   */
  createEstimate: (
    formData: T,
    onStepProgress?: StepProgressCallback
  ) => Promise<number | { id: number } | any>;
  onComplete: (createdIds: number[]) => void;
  onAllFailed?: () => void;
  /**
   * Optional steps to show for each estimate.
   * If provided, each estimate card will show these sub-steps.
   */
  steps?: Step[];
  flow?: 'drawing' | 'measurement' | 'scratch' | 'template';
};

const DEFAULT_STEPS: Step[] = [
  { id: 'estimate', label: 'Estimate', icon: 'calculator' },
];

const MEASUREMENT_STEPS: Step[] = [
  { id: 'estimate', label: 'Estimate', icon: 'calculator' },
  { id: 'matorder', label: 'Matorder', icon: 'material' },
  { id: 'workorder', label: 'Workorder', icon: 'workorder' },
];

type StepStatusRecord = Record<string, { status: StepStatus; error?: string }>;
type EstimateStatusRecord = Record<number, StepStatusRecord>;

function EstimateCreationProgress<T>({
  estimates,
  createEstimate,
  onComplete,
  onAllFailed,
  steps,
  flow,
}: EstimateCreationProgressProps<T>) {
  const { userInfo } = useAuth();
  const activeSteps = steps || DEFAULT_STEPS;
  const hasMultipleSteps = activeSteps.length > 1;
  const { jobId = '' } = useParams();

  const [creationStatus, setCreationStatus] = useState<EstimateStatusRecord>(
    () => {
      const initial: EstimateStatusRecord = {};
      estimates.forEach((est) => {
        const stepStatus: StepStatusRecord = {};
        activeSteps.forEach((step) => {
          stepStatus[step.id] = { status: 'queued' };
        });
        initial[est.id] = stepStatus;
      });
      return initial;
    }
  );

  const hasStartedRef = useRef(false);

  // Helper to update step status
  const updateStepStatus = (
    estimateId: number,
    stepId: string,
    status: StepStatus,
    error?: string
  ) => {
    setCreationStatus((prev) => ({
      ...prev,
      [estimateId]: {
        ...prev[estimateId],
        [stepId]: { status, error },
      },
    }));
  };

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const runCreation = async () => {
      const createdEstimateIds: number[] = [];
      let hasFailed = false;

      for (const estimate of estimates) {
        // Stop if a previous estimate failed
        if (hasFailed) break;

        // Create progress callback for this estimate
        const onStepProgress: StepProgressCallback = (
          stepId,
          status,
          error
        ) => {
          updateStepStatus(estimate.id, stepId, status, error);
        };

        // Mark first step as in progress if no steps callback will be used
        if (!hasMultipleSteps) {
          updateStepStatus(estimate.id, activeSteps[0].id, 'in_progress');
        }

        try {
          const result = await createEstimate(
            estimate.formData,
            onStepProgress
          );

          if (!result) {
            throw new Error('No result returned');
          }

          // If single step mode, mark as created
          if (!hasMultipleSteps) {
            updateStepStatus(estimate.id, activeSteps[0].id, 'created');
          }

          // Handle both cases: result is a number (just the id) or an object with id property
          const createdId = typeof result === 'number' ? result : result?.id;
          if (createdId) {
            createdEstimateIds.push(createdId);
            trackEvent(
              'Estimate Created',
              {
                job_id: jobId,
                estimate_id: createdId,
                flow: flow,
                profit: result?.overview?.profit,
                totalContractAmount: result?.overview?.gt_price,
              },
              userInfo
            );
          }

          // Wait 1 second to show the final check before moving to next estimate
          await new Promise((resolve) => setTimeout(resolve, 1000));
        } catch (err: any) {
          hasFailed = true;
          const errorMessage =
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to create estimate';

          // If single step mode, mark as failed
          if (!hasMultipleSteps) {
            updateStepStatus(
              estimate.id,
              activeSteps[0].id,
              'failed',
              errorMessage
            );
          }
          // Stop processing - don't continue to next estimate
          break;
        }

        // Small delay between estimates
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Only call callbacks if no failures occurred
      if (!hasFailed) {
        if (createdEstimateIds.length > 0) {
          // Wait 2 seconds to show "Done!" state before redirecting
          await new Promise((resolve) => setTimeout(resolve, 2000));
          onComplete(createdEstimateIds);
        } else {
          onAllFailed?.();
        }
      }
      // If failed, stay on this screen showing the error
    };

    runCreation();
  }, [
    estimates,
    createEstimate,
    onComplete,
    onAllFailed,
    activeSteps,
    hasMultipleSteps,
  ]);

  // Count completed steps for an estimate
  const getCompletedStepsCount = (estimateId: number): number => {
    const stepStatuses = creationStatus[estimateId];
    if (!stepStatuses) return 0;

    return Object.values(stepStatuses).filter((s) => s.status === 'created')
      .length;
  };

  // Calculate total completed steps across all estimates
  const getTotalCompletedSteps = (): number => {
    let total = 0;
    estimates.forEach((est) => {
      total += getCompletedStepsCount(est.id);
    });
    return total;
  };

  const totalSteps = estimates.length * activeSteps.length;
  const completedSteps = getTotalCompletedSteps();

  // Get failed step info
  const getFailedStep = (): {
    estimateIndex: number;
    stepIndex: number;
    error?: string;
  } | null => {
    for (let i = 0; i < estimates.length; i++) {
      const est = estimates[i];
      for (let j = 0; j < activeSteps.length; j++) {
        const step = activeSteps[j];
        const stepInfo = creationStatus[est.id]?.[step.id];
        if (stepInfo?.status === 'failed') {
          return { estimateIndex: i, stepIndex: j, error: stepInfo.error };
        }
      }
    }
    return null;
  };

  const failedStep = getFailedStep();
  const isComplete = completedSteps === totalSteps;

  // Handle refresh page
  const handleRefreshPage = () => {
    window.location.reload();
  };

  // Show error modal when failed
  if (failedStep) {
    const errorMessage = failedStep.error;
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-8">
        <div className="bg-white rounded-xl p-0 min-w-[300px] text-center">
          <div className="text-2xl font-bold text-text-dark mb-2">
            Something went wrong!
          </div>
          <div>
            {errorMessage ||
              'We encountered an error while creating your estimate. Please try again.'}
            <br />
            Refresh the page to try again.
          </div>
        </div>
        <div className="flex justify-center">
          <Icon name="alert-circle" size={64} className="text-error" />
        </div>
        <div className="w-full max-w-sm">
          <Button
            label="Refresh Page"
            leftIcon="refresh"
            iconSize={28}
            variant={Variants.Outline}
            onClick={handleRefreshPage}
            className="font-bold"
          />
        </div>
      </div>
    );
  }

  // Count completed estimates (all steps done for an estimate)
  const getCompletedEstimatesCount = (): number => {
    return estimates.filter((est) => {
      const stepStatuses = creationStatus[est.id];
      if (!stepStatuses) return false;
      return Object.values(stepStatuses).every((s) => s.status === 'created');
    }).length;
  };

  const completedEstimates = getCompletedEstimatesCount();
  const totalEstimates = estimates.length;

  // Get the current estimate being processed (first one that's not fully complete)
  const getCurrentEstimate = (): EstimateToCreate<T> | null => {
    for (const est of estimates) {
      const stepStatuses = creationStatus[est.id];
      if (!stepStatuses) return est;

      const allDone = Object.values(stepStatuses).every(
        (s) => s.status === 'created'
      );
      const hasFailed = Object.values(stepStatuses).some(
        (s) => s.status === 'failed'
      );

      if (!allDone || hasFailed) {
        return est;
      }
    }
    return estimates[estimates.length - 1]; // Return last if all complete
  };

  const currentEstimate = getCurrentEstimate();

  // Build step labels for ONLY the current estimate
  // Fade previous steps when a later step has started (not queued anymore)
  const getStepLabelsForCurrentEstimate = () => {
    if (!currentEstimate) return [];

    const labels: {
      label: string;
      status: 'current' | 'pending' | 'done' | 'done_faded' | 'failed';
    }[] = [];

    // Get all step statuses
    const stepStatuses = activeSteps.map(
      (step) =>
        creationStatus[currentEstimate.id]?.[step.id]?.status || 'queued'
    );

    for (let i = 0; i < activeSteps.length; i++) {
      const step = activeSteps[i];
      const status = stepStatuses[i];

      let displayStatus:
        | 'current'
        | 'pending'
        | 'done'
        | 'done_faded'
        | 'failed';

      if (status === 'created') {
        // Fade if ANY step after this one has started (is not 'queued')
        const hasLaterStepStarted = stepStatuses
          .slice(i + 1)
          .some((s) => s !== 'queued');
        displayStatus = hasLaterStepStarted ? 'done_faded' : 'done';
      } else if (status === 'failed') {
        displayStatus = 'failed';
      } else if (status === 'in_progress') {
        displayStatus = 'current';
      } else {
        displayStatus = 'pending';
      }

      labels.push({
        label: `Creating ${step.label}`,
        status: displayStatus,
      });
    }
    return labels;
  };

  const stepLabels = getStepLabelsForCurrentEstimate();

  // Completed state - show "Done!" screen
  if (isComplete) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="bg-white rounded-xl p-0 min-w-[300px] text-center">
          <div className="text-2xl font-bold text-text-dark mb-6">Done!</div>
          <div className="flex justify-center">
            <svg
              className="w-20 h-20"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="50"
                cy="50"
                r="42"
                stroke="#3B82F6"
                strokeWidth="6"
                fill="none"
              />
              <path
                d="M30 50 L45 65 L70 35"
                stroke="#3B82F6"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-4">
      <div className="bg-white rounded-lg min-w-[300px] text-center">
        {/* Step labels */}
        <div className="space-y-7 mb-6">
          {stepLabels.map((item, idx) => (
            <div
              key={idx}
              className={clx(
                'text-xl transition-all duration-500 flex items-center justify-center gap-2',
                {
                  'font-bold text-text-dark': item.status === 'current',
                  'text-[#D1D5DB] font-medium': item.status === 'pending',
                  'text-primary font-bold': item.status === 'done',
                  'text-[#D1D5DB] font-bold': item.status === 'done_faded',
                  'text-error font-medium': item.status === 'failed',
                }
              )}
            >
              {item.label}
              {(item.status === 'done' || item.status === 'done_faded') && (
                <Icon
                  name="check-circle-alt"
                  size={20}
                  className={clx('transition-all duration-500', {
                    'text-primary': item.status === 'done',
                    'text-[#D1D5DB]': item.status === 'done_faded',
                  })}
                />
              )}
            </div>
          ))}
        </div>
        {/* Circular Progress Indicator */}
        <div className="flex justify-center mb-4">
          <svg
            className="animate-spin w-20 h-20"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Light background ring */}
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="#BFDBFE"
              strokeWidth="4"
            />
            {/* Dark spinning arc */}
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="#3B82F6"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="80 126"
              transform="rotate(-90 25 25)"
            />
          </svg>
        </div>
        {/* Progress counter - shows estimate count */}
        <div className="text-base font-semibold text-primary">
          {completedEstimates}/{totalEstimates} Complete
        </div>
      </div>
    </div>
  );
}

// Export steps configurations for reuse
export { DEFAULT_STEPS, MEASUREMENT_STEPS };
export type { Step, EstimateToCreate, StepProgressCallback };

export default EstimateCreationProgress;
